// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCsvExport } from '@hooks/other/useCsvExport';
import { useDialogControl } from '@hooks/other/useDialogControl';
import React, { useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import { Mint } from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Model/Mint';
import { Batch } from '@daml.js/dtc-registry/lib/Dtc/Registry/V3/Types';
import CopyableText from '../../../components/CopyableText';
import Loading from '../../../components/Loading';
import PartyDisplay from '../../../components/PartyDisplay';
import { StateCell, Status } from '../../../components/StateCell';
import { DataTable } from '../../../components/Table/DataTable';
import { useExecutedMints2Step } from '../../../hooks/queries/registry/mint/useExecutedMints2Step';
import { useRejectedMints2Step } from '../../../hooks/queries/registry/mint/useRejectedMints2Step';
import useRegistrarService from '../../../hooks/queries/registry/onboarding/useRegistrarService';
import {
  InstrumentsById,
  useInstrumentConfigurationsById,
} from '../../../hooks/queries/useGetAllInstrumentConfigurations';
import { fmt, formatTimestamp, parseTimestampToDate } from '../../../utils/common';
import { PageWrapper } from '../../PageWrapper';
import { RegistrarMintDialog } from './RegistrarMintDialog';

type Row = {
  contractId: string;
  state: string;
  registrar: string;
  payload: Mint;
  reason?: string;
};

const createHeaders: (instrumentsById: InstrumentsById) => GridColDef<Row>[] = (
  instrumentsById,
) => [
  {
    field: 'state',
    headerName: 'State',
    renderCell: (params) => {
      return <StateCell state={params.row.state as Status} reason={params.row.reason} />;
    },
    flex: 100,
    filterable: true,
    valueGetter: (params) => params.row.state,
  },
  {
    field: 'registrar',
    headerName: 'Registrar',
    renderCell: (params) => <PartyDisplay party={params.row.registrar} />,
    valueGetter: (params) => params.row.registrar,
    flex: 100,
    filterable: false,
  },
  {
    field: 'holder',
    headerName: 'Holder',
    renderCell: (params) => <PartyDisplay party={params.row.payload.holder} />,
    valueGetter: (params) => params.row.payload.holder,
    flex: 100,
    filterable: false,
  },
  {
    field: 'instrumentId',
    headerName: 'Instrument Id',
    valueGetter: (params) => params.row.payload.instrumentId.id,
    renderCell: (params) => <CopyableText value={params.row.payload.instrumentId.id} />,
    flex: 150,
    filterable: true,
  },
  {
    field: 'tokenName',
    headerName: 'Token Name',
    valueGetter: (params) =>
      instrumentsById[params.row.payload.instrumentId.id]?.payload.tokenName || '',
    flex: 150,
    filterable: true,
  },
  {
    field: 'tokenSymbol',
    headerName: 'Token Symbol',
    valueGetter: (params) =>
      instrumentsById[params.row.payload.instrumentId.id]?.payload.tokenSymbol || '',
    flex: 150,
    filterable: true,
  },
  {
    field: 'cusip',
    headerName: 'CUSIP',
    valueGetter: (params) =>
      instrumentsById[params.row.payload.instrumentId.id]?.payload.cusip || '',
    flex: 150,
    filterable: true,
  },
  {
    field: 'amount',
    headerName: 'Amount',
    renderCell: (params) => fmt(params.row.payload.amount, 2),
    valueGetter: (params) => Number(params.row.payload.amount),
    flex: 150,
    filterable: true,
  },
  {
    field: 'reference',
    headerName: 'Reference',
    valueGetter: (params) => params.row.payload.reference,
    flex: 150,
    filterable: true,
  },
  {
    field: 'requestedAt',
    headerName: 'Requested At',
    type: 'dateTime',
    valueGetter: (params) => parseTimestampToDate(params.row.payload.requestedAt),
    renderCell: (params) => formatTimestamp(params.row.payload.requestedAt),
    flex: 150,
    minWidth: 180,
  },
  {
    field: 'executeBefore',
    headerName: 'Execute Before',
    type: 'dateTime',
    valueGetter: (params) => parseTimestampToDate(params.row.payload.executeBefore),
    renderCell: (params) => formatTimestamp(params.row.payload.executeBefore),
    flex: 150,
    minWidth: 180,
  },
];

interface MintsProps {
  disableVirtualization?: boolean;
}

export const Mints: React.FC<MintsProps> = ({ disableVirtualization }) => {
  const registrarService = useRegistrarService();

  const rejectedMints2Step = useRejectedMints2Step();
  const executedMints2Step = useExecutedMints2Step();
  const instrumentsById = useInstrumentConfigurationsById();

  const { setDialogName, dialogName, onCloseDialog } = useDialogControl();

  const isLoading =
    registrarService.isLoading || rejectedMints2Step.isLoading || executedMints2Step.isLoading;

  const columns = useMemo(() => createHeaders(instrumentsById), [instrumentsById]);

  const rows = useMemo(() => {
    return rejectedMints2Step.data
      .map<Row>((r) => ({
        contractId: r.contractId,
        state: 'Rejected2Step',
        reason: r.payload.reason,
        registrar: r.payload.mint.instrumentId.admin,
        payload: {
          ...r.payload.mint,
          operator: r.payload.operator,
          batch: { id: '' } as Batch,
          instrumentId: {
            ...r.payload.mint.instrumentId,
            scheme: '',
            source: r.payload.mint.instrumentId.admin,
          },
        },
      }))
      .concat(
        executedMints2Step.data.map((r) => ({
          contractId: r.contractId,
          state: 'Executed2Step',
          registrar: r.payload.mint.instrumentId.admin,
          payload: {
            ...r.payload.mint,
            operator: r.payload.operator,
            batch: { id: '' } as Batch,
            instrumentId: {
              ...r.payload.mint.instrumentId,
              scheme: '',
              source: r.payload.mint.instrumentId.admin,
            },
          },
        })),
      );
  }, [rejectedMints2Step.data, executedMints2Step.data]);

  const isRegistrar = !!registrarService.data;
  const { handleExport, isExportDisabled } = useCsvExport(columns, rows, {
    filename: 'mints',
  });
  const buttons = [
    {
      name: 'Export CSV',
      onClick: handleExport,
      disabled: isExportDisabled,
    },
    ...(isRegistrar
      ? [
          {
            name: 'Registrar Mint',
            onClick: () => setDialogName('registrar-mint'),
          },
        ]
      : []),
  ];

  if (isLoading) return <Loading />;

  return (
    <PageWrapper buttons={buttons} pageName="Mints">
      {dialogName === 'registrar-mint' && <RegistrarMintDialog open onClose={onCloseDialog} />}
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(a) => a.contractId}
        disableVirtualization={disableVirtualization}
      />
    </PageWrapper>
  );
};
